/**
 * BNPL payment screen (ERS-78 screen 09)
 *
 * Route: `/webAppOnboarding/insurance-bnpl-payment`. Cost table totals, "Zahlungsart" choice
 * (Physiofit Abrechnungsservice or Kauf auf Rechnung), the process detail (drawer or inline
 * timeline, resolved per insurer by `resolveBnplDetailStyle` until step 2 wires the A/B flag), the two consents, and
 * the submit. Replaces the consent half of the former `insurance-bnpl-checkout` screen; the personal
 * data form stays on 08.
 *
 * Two exits rewrite `insuranceInfo.billingType` and both suppress the explain guard first via
 * `exitHandledRef` (the ERS-5 race, see `useInsuranceExplainBillingGuard`): a provider decline
 * (reverts to the insurer's catalog default, `useBnplPaymentScreen`'s `revertToInsurerDefault`) and
 * the "Kauf auf Rechnung" opt-out (writes the literal `'invoice'`).
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard` (callables use signed-in session).
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local (inside the hook): `useInsuranceExplainBillingGuard('bnpl', { suppressedRef })`;
 *   `useSaveOnboardingStep('insurance_bnpl_payment')`; a completeness guard that replaces to 08 when
 *   `privateInfo` is not complete (deep link, resumed session).
 */

import type { MouseEvent } from 'react';
import { Fragment } from 'react';
import styled from 'styled-components';
import { colors } from '../../../styles/colors';
import { fontFamily, space, text } from '../../../styles/onboardingUiTokens';
import { CompactTimeline } from '../components/content/CompactTimeline';
import { OnboardingHeroBanner } from '../components/content/OnboardingHeroBanner';
import { InsuranceBnplBillingDetailDrawer } from '../components/content/InsuranceBnplBillingDetailDrawer';
import { LiabilityLegalDrawer } from '../components/content/LiabilityLegalDrawer';
import { OnboardingCheckoutTable } from '../components/content/OnboardingCheckoutTable';
import { CARD_TABLE_COLORS } from '../components/content/checkoutTableColors';
import { LoadingSpinner } from '../components/elements/LoadingSpinner';
import { Notice } from '../components/elements/Notice';
import { OnboardingBusyOverlay } from '../components/elements/OnboardingBusyOverlay';
import { Checkbox } from '../components/forms/Checkbox';
import { OptionCard, OptionCardGroup, OptionCardSectionLabel } from '../components/forms/OptionCard';
import { SvgIcon } from '../components/icons/SvgIcon';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { OnboardingSupportHint } from '../components/layout/OnboardingSupportHint';
import { Body } from '../components/typography/Body';
import { H1 } from '../components/typography/HeadingLevels';
import { useBnplPaymentScreen } from '../hooks/useBnplPaymentScreen';
import { REQUIRED_COURSE_UNITS } from '../hooks/useCourseContent';
import {
  BNPL_CHECKOUT_CONSENT_OUTDATED_NOTICE,
  BNPL_CHECKOUT_GENERIC_ERROR_NOTICE,
  BNPL_CHECKOUT_INVALID_DATA_ERROR_NOTICE,
  BNPL_CHECKOUT_LEGAL_DOCUMENT_UNAVAILABLE_NOTICE,
  BNPL_CHECKOUT_LIABILITY_CHECKBOX_TEXT,
  BNPL_CHECKOUT_PROVIDER_PRIVACY_CHECKBOX_TEXT,
  BNPL_CHECKOUT_SUBMIT_PROGRESS_ARIA_LABEL,
  BNPL_CHECKOUT_SUBMIT_PROGRESS_MESSAGES,
  BNPL_COST_TABLE_LABELS,
  BNPL_PAYMENT_CTA,
  BNPL_PAYMENT_DETAIL_TRIGGER_LABEL,
  BNPL_PAYMENT_HERO_TITLE,
  BNPL_PAYMENT_INFO_CARD,
  BNPL_PAYMENT_METHOD_COPY,
  BNPL_PAYMENT_SUBTITLE,
  buildBnplPaymentCtaHint,
} from '../insurance/bnplCopy';
import { formatEuro } from '../insurance/copyUtils';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
`;

const DynamicLoadingArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  width: 100%;
`;

const ConsentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.sm};
`;

/** Figma draws this as a muted underlined text button with a trailing arrow, not a primary link. */
const DetailTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 3px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.button.fontSize};
  line-height: ${text.button.lineHeight};
  font-weight: 500;
  color: ${colors.medium};

  & > span:first-child {
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &:hover {
    color: ${colors.dark};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const CtaHint = styled.span`
  font-size: ${text.mini.fontSize};
  font-weight: 400;
`;

const linkStyle = { color: colors.primary, textDecoration: 'underline' as const };

/**
 * Three states, in the order the provider can deliver its privacy document: a URL opens in a tab, an
 * inline document opens the drawer, and neither renders as plain text. The last one is the ERS-75
 * placeholder, where the cached `consentText` is only an acknowledgement line and there is nothing
 * to open; it resolves itself once the provider issues a real document.
 */
function ConsentLink({ href, onOpen, children }: { href?: string; onOpen?: () => void; children: React.ReactNode }) {
  const trimmed = href?.trim();
  if (trimmed) {
    return (
      <a href={trimmed} target="_blank" rel="noreferrer" style={linkStyle}>
        {children}
      </a>
    );
  }
  if (!onOpen) {
    return <>{children}</>;
  }
  return (
    <a
      href="#"
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        onOpen();
      }}
      style={linkStyle}
    >
      {children}
    </a>
  );
}

export function WebOnboardingInsuranceBnplPaymentScreen() {
  const payment = useBnplPaymentScreen();
  const {
    insurance,
    insuranceLoading,
    insuranceError,
    legalResult,
    bnplLegal,
    coverage,
    insuranceShortName,
    providerConsentShown,
    consentLink,
    inlineConsentDocument,
    isProviderPrivacyDrawerOpen,
    setIsProviderPrivacyDrawerOpen,
    consentWasUpdated,
    paymentMethod,
    handleSelectPaymentMethod,
    liabilityAccepted,
    setLiabilityAccepted,
    providerPrivacyAccepted,
    setProviderPrivacyAccepted,
    isDetailOpen,
    setIsDetailOpen,
    isDetailExpanded,
    consentError,
    consentSectionRef,
    isSubmitting,
    submitError,
    forwardDisabled,
    forwardInactive,
    detailStyle,
    timeline,
    patientSubmits,
    ctaAmount,
    handleBack,
    handleForward,
    handleOpenDetail,
  } = payment;

  const bodyContent = (() => {
    if (insuranceLoading || legalResult.status === 'loading' || bnplLegal.status === 'loading') {
      return (
        <DynamicLoadingArea>
          <LoadingSpinner color={colors.primary} size={32} />
        </DynamicLoadingArea>
      );
    }

    if (insuranceError) {
      return (
        <Notice preset="warning" title="Krankenkasse">
          {insuranceError}
        </Notice>
      );
    }

    if (!insurance || !coverage) {
      return (
        <Notice preset="warning" title="Krankenkasse">
          Bitte wähle zuerst deine Krankenkasse aus.
        </Notice>
      );
    }

    if (legalResult.status === 'error') {
      return (
        <Notice preset="warning" title="Rechtliche Informationen">
          Die rechtlichen Informationen konnten nicht geladen werden.
        </Notice>
      );
    }

    return (
      <ContentColumn>
        <Body variant="document" tone="muted">
          {BNPL_PAYMENT_SUBTITLE}
        </Body>

        {providerConsentShown && bnplLegal.status === 'unavailable' ? (
          <Notice preset="warning" title="Datenschutzbestimmung">
            {BNPL_CHECKOUT_LEGAL_DOCUMENT_UNAVAILABLE_NOTICE}
          </Notice>
        ) : null}
        {consentWasUpdated ? (
          <Notice preset="warning" title="Datenschutzbestimmung aktualisiert">
            {BNPL_CHECKOUT_CONSENT_OUTDATED_NOTICE}
          </Notice>
        ) : null}

        <OptionCardGroup legend={BNPL_PAYMENT_METHOD_COPY.legend} hint={BNPL_PAYMENT_METHOD_COPY.hint}>
          <OptionCardSectionLabel badge={BNPL_PAYMENT_METHOD_COPY.recommendedBadge}>
            {BNPL_PAYMENT_METHOD_COPY.bnplSectionLabel}
          </OptionCardSectionLabel>
          <OptionCard
            name="paymentMethod"
            value="bnpl"
            checked={paymentMethod === 'bnpl'}
            onSelect={handleSelectPaymentMethod}
            title={BNPL_PAYMENT_METHOD_COPY.bnplTitle}
            description={BNPL_PAYMENT_METHOD_COPY.bnplDescription}
          >
            <DetailTrigger type="button" onClick={handleOpenDetail}>
              <span>{BNPL_PAYMENT_DETAIL_TRIGGER_LABEL}</span>
              <SvgIcon name="arrowright" size={24} primaryColor={colors.medium} />
            </DetailTrigger>
            {detailStyle === 'inline' && isDetailExpanded ? (
              <>
                <CompactTimeline items={timeline} />
                <OnboardingSupportHint variant="compact" bottomSpacing="none" />
              </>
            ) : null}
          </OptionCard>

          <OptionCardSectionLabel>{BNPL_PAYMENT_METHOD_COPY.invoiceSectionLabel}</OptionCardSectionLabel>
          <OptionCard
            name="paymentMethod"
            value="invoice"
            checked={paymentMethod === 'invoice'}
            onSelect={handleSelectPaymentMethod}
            title={BNPL_PAYMENT_METHOD_COPY.invoiceTitle}
            description={BNPL_PAYMENT_METHOD_COPY.invoiceDescription}
          />
        </OptionCardGroup>
        <OnboardingCheckoutTable
          showFooter
          showPhysiofitSubsidy={!coverage.isFullCoverage}
          footerValueEmphasis
          secondaryFooter={{ label: BNPL_COST_TABLE_LABELS.dueToday, value: formatEuro(0, 'plain') }}
          colors={CARD_TABLE_COLORS}
          copy={{
            a11ySummary: BNPL_COST_TABLE_LABELS.a11ySummary,
            courseFeeLabel: BNPL_COST_TABLE_LABELS.courseFee(REQUIRED_COURSE_UNITS),
            courseFeeValue: formatEuro(coverage.courseFeeEur, 'plain'),
            insurerRefundLabel: BNPL_COST_TABLE_LABELS.insurerRefund,
            insurerRefundValue: formatEuro(coverage.insurerRefundEur, 'deduct'),
            physiofitSubsidyLabel: BNPL_COST_TABLE_LABELS.physiofitSubsidy,
            physiofitSubsidyValue: formatEuro(coverage.physiofitSubsidyEur, 'deduct'),
            footerLabel: BNPL_COST_TABLE_LABELS.totalForYou,
            footerValue: formatEuro(0, 'plain'),
          }}
        />

        <Notice preset="success" icon={<SvgIcon name="shield" size={24} primaryColor={colors.success} decorative />}>
          {BNPL_PAYMENT_INFO_CARD}
        </Notice>

        <ConsentStack ref={consentSectionRef}>
          {consentError ? (
            <Notice preset="warning" title="Bitte bestätigen">
              Bitte bestätige die Nutzungsbedingungen um fortzufahren
            </Notice>
          ) : null}
          {providerConsentShown ? (
            <Checkbox
              checked={providerPrivacyAccepted}
              onCheckedChange={setProviderPrivacyAccepted}
              error={consentError}
            >
              {BNPL_CHECKOUT_PROVIDER_PRIVACY_CHECKBOX_TEXT.split('Datenschutzbestimmung der RBI').map(
                (part, idx, arr) => (
                  <Fragment key={idx}>
                    {part}
                    {idx < arr.length - 1 ? (
                      <ConsentLink
                        href={consentLink}
                        onOpen={inlineConsentDocument ? () => setIsProviderPrivacyDrawerOpen(true) : undefined}
                      >
                        Datenschutzbestimmung der RBI
                      </ConsentLink>
                    ) : null}
                  </Fragment>
                ),
              )}
            </Checkbox>
          ) : null}
          <Checkbox checked={liabilityAccepted} onCheckedChange={setLiabilityAccepted} error={consentError}>
            {BNPL_CHECKOUT_LIABILITY_CHECKBOX_TEXT}
          </Checkbox>
        </ConsentStack>

        {submitError ? (
          <Notice preset="warning" title="Konnte nicht abgeschlossen werden">
            {submitError.kind === 'invalid-argument'
              ? BNPL_CHECKOUT_INVALID_DATA_ERROR_NOTICE
              : BNPL_CHECKOUT_GENERIC_ERROR_NOTICE}
          </Notice>
        ) : null}
      </ContentColumn>
    );
  })();

  return (
    <OnboardingLayout
      currentStep={6}
      totalSteps={7}
      forwardLabel={
        <>
          {BNPL_PAYMENT_CTA} <CtaHint>{buildBnplPaymentCtaHint(ctaAmount)}</CtaHint>
        </>
      }
      forwardDisabled={forwardDisabled}
      forwardInactive={forwardInactive}
      forwardBusy={isSubmitting}
      onBack={handleBack}
      onForward={handleForward}
    >
      <OnboardingHeader />
      <OnboardingHeroBanner />
      <OnboardingBody>
        <H1>{BNPL_PAYMENT_HERO_TITLE}</H1>
        <div style={{ paddingBottom: space.xl, paddingTop: space.sm }}>{bodyContent}</div>
      </OnboardingBody>
      <OnboardingFooter />
      {/*
        The submit runs five provider round trips inside one callable, so the wait is seconds long.
        `forwardBusy` alone only sets `aria-busy` on the CTA — nothing visible — which reads as
        "nothing happened" on the one screen where the patient has just committed.
      */}
      <OnboardingBusyOverlay
        open={isSubmitting}
        messages={BNPL_CHECKOUT_SUBMIT_PROGRESS_MESSAGES}
        ariaLabel={BNPL_CHECKOUT_SUBMIT_PROGRESS_ARIA_LABEL}
      />
      {providerConsentShown && inlineConsentDocument ? (
        <LiabilityLegalDrawer
          open={isProviderPrivacyDrawerOpen}
          onClose={() => setIsProviderPrivacyDrawerOpen(false)}
          title="Datenschutzbestimmung der RBI"
          text={inlineConsentDocument.consentText}
        />
      ) : null}
      {coverage && insuranceShortName ? (
        <InsuranceBnplBillingDetailDrawer
          open={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          insuranceShortName={insuranceShortName}
          coverage={coverage}
          hasBonus={Boolean(insurance?.bonus?.hasBonus)}
          bonusDescription={insurance?.bonus?.description}
          patientSubmits={patientSubmits}
        />
      ) : null}
    </OnboardingLayout>
  );
}
